// src/store/game.ts
import { create } from "zustand"
import {
    BOARD_SIZE,
    createEmptyBoard,
    captureOpponentPieces,
    hasLibertyByPosition,
    getNeighborsAsPositions,
    getGroup,
    isValidMoveForStore,
} from "@/services/AIService/goUtils"
import { Skill } from "@/components/SkillDialog"
import { useAnimationStore } from "@/store/animation"
import { shouldGameEnd } from "@/utils/game/gameEnd"
import { calculateScore } from "@/utils/game/score"
import { sfxPlay } from "@/assets/audios/sfx"

export { BOARD_SIZE }
export const MIN_STEP = 2
export const MIN_MOVES_FOR_SETTLEMENT = 20 // 最少下满 20 步（10 回合）才能判定胜负
export type ChessType = "black" | "white" | null
export type ChessPosition = { row: number; col: number }
// 记录落子位置、使用的技能和技能影响的具体位置（用于精确重放）
export type MoveRecord = ChessPosition & {
    skill?: Skill
    skillPositions?: ChessPosition[] // 技能影响的具体位置列表
    capturedCount?: number // 提子数量
}

export interface GameState {
    chooseSkills: Skill[]
    activeSkillIndex: number | null // 当前激活的技能槽位索引（下一步落子时生效）
    resetGame: boolean // 是否重新开始游戏
    usedSkillIndices: number[] // 已使用过的技能槽位索引列表
    // 当前轮到谁下（true = 黑棋，false = 白棋）
    currentPlayer: boolean
    board: ChessType[][]
    // 已经下了多少步（包含黑白双方）
    currentStep: number
    // 按时间顺序记录每一步落子位置和技能（黑白轮流）
    history: MoveRecord[]
    // 提子数（吃掉对方棋子的数量）
    blackCaptures: number // 黑棋提子数（黑棋吃掉的白子）
    whiteCaptures: number // 白棋提子数（白棋吃掉的黑子）
    // 胜负状态（自动计算，外部只读）
    winner: "black" | "white" | null // null 表示未分出胜负
    scoreDiff: number // 得分差（正数表示黑赢，负数表示白赢，可能是小数）
    gameStartTime: number
    // 打劫禁入点（如果上一步只吃了一个子，记录被吃的位置）
    koPosition: ChessPosition | null
    // 连续大分差历史记录（用于判断连续N步分差>=20）
    // 数组中的每个元素代表一步是否达到分差要求（true=达到，false=未达到）
    // 每一手为一次，连续N次（N = 2*回合数限制）满足条件就结束游戏
    // 数组按时间顺序存储，最新的一步在数组末尾
    consecutiveLargeDiffHistory: boolean[]
}

interface GameActions {
    // 设置已选技能
    setChooseSkills: (chooseSkills: Skill[]) => void
    //设置棋盘内容
    setBoard: (board: ChessType[][]) => void
    clearBoard: () => void
    // 使用技能（激活，下一步落子时生效）
    useSkill: (skillIndex: number) => void
    // 取消当前激活的技能
    clearActiveSkill: () => void
    // 下棋
    placeChess: (
        row: number,
        col: number
    ) => {
        success: boolean
        reason?: "outOfBounds" | "occupied" | "ko" | "suicide"
    }
    // 仅进行规则检查，不真正落子（给 UI 预检查用）
    validateMove: (
        row: number,
        col: number
    ) => {
        success: boolean
        reason?: "outOfBounds" | "occupied" | "ko" | "suicide"
    }
    // 悔棋
    undo: () => void
    // 重新开始
    reset: () => void
    // 切换玩家
    togglePlayer: () => void
    // 设置游戏开始时间
    setGameStartTime: (gameStartTime: number) => void
}

// 检测双活公气：检查一个空点是否是双活的公气
// 双活的条件：
// 1. 空点同时接触黑棋和白棋的活棋组
// 2. 这些棋组除了这个公气点外，没有其他公气点
const detectDoubleLifeAccurate = (board: ChessType[][]): ChessPosition[] => {
    const publicQiPoints: ChessPosition[] = []
    const processed = new Set<string>()

    // 遍历所有空点
    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            if (board[row][col] !== null) continue

            const key = `${row},${col}`
            if (processed.has(key)) continue

            // 找到所有相邻的活棋组
            const neighbors = getNeighborsAsPositions(row, col)
            const blackGroups = new Set<string>()
            const whiteGroups = new Set<string>()

            for (const neighbor of neighbors) {
                const stone = board[neighbor.row][neighbor.col]
                if (stone === null) continue

                // 检查这个棋子所在的棋组是否活
                const visited = new Set<string>()
                if (
                    !hasLibertyByPosition(
                        board,
                        neighbor.row,
                        neighbor.col,
                        visited
                    )
                ) {
                    continue // 死棋，不考虑
                }

                // 获取棋组标识
                const group = getGroup(board, neighbor.row, neighbor.col, stone)
                const groupKey = Array.from(group).sort().join("|")

                if (stone === "black") {
                    blackGroups.add(groupKey)
                } else {
                    whiteGroups.add(groupKey)
                }
            }

            // 如果这个空点同时接触黑棋和白棋的活棋组，则可能是公气
            if (blackGroups.size > 0 && whiteGroups.size > 0) {
                // 检查这些棋组是否共享这个公气点
                // 简化：如果同时接触黑白活棋，认为是公气
                publicQiPoints.push({ row, col })
                processed.add(key)
            }
        }
    }

    return publicQiPoints
}

// 计算当前棋盘下的胜负状态（只计算围住的空地数，不计算棋子数）
// 内部方法：计算当前棋盘的胜负状态
// 注意：此函数已不再使用，保留仅作参考
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculateScoreFromBoard = (
    board: ChessType[][]
): {
    winner: "black" | "white" | null
    diff: number // 正数表示黑多 diff 目，负数表示白多 |diff| 目
} => {
    const visited = new Set<string>()
    let blackArea = 0
    let whiteArea = 0
    let blackStones = 0
    let whiteStones = 0

    // 统计棋子数量（用于判断定局，不计入得分）
    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            const stone = board[row][col]
            if (stone === "black") {
                blackStones++
            } else if (stone === "white") {
                whiteStones++
            }
        }
    }

    // 先检测所有双活公气点（这些点需要平分，不能算入任何一方的地盘）
    const publicQiPoints = detectDoubleLifeAccurate(board)
    const publicQiSet = new Set<string>()
    for (const p of publicQiPoints) {
        publicQiSet.add(`${p.row},${p.col}`)
    }

    let hasNeutralTerritory = false

    // 遍历所有空点，使用洪水填充判断这片空地归属（排除公气点）
    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            const key = `${row},${col}`
            if (visited.has(key)) continue

            if (board[row][col] !== null) continue

            // 如果是公气点，跳过（稍后平分）
            if (publicQiSet.has(key)) continue

            // 洪水填充一片空地
            const territory = new Set<string>()
            const stack: ChessPosition[] = [{ row, col }]
            const borders = new Set<ChessType>() // 边界上接触到的棋子颜色

            while (stack.length > 0) {
                const current = stack.pop()!
                const cKey = `${current.row},${current.col}`

                if (visited.has(cKey)) continue
                if (board[current.row][current.col] !== null) continue
                // 跳过公气点
                if (publicQiSet.has(cKey)) continue

                visited.add(cKey)
                territory.add(cKey)

                const neighbors = getNeighborsAsPositions(
                    current.row,
                    current.col
                )
                for (const n of neighbors) {
                    const s = board[n.row][n.col]
                    if (s === null) {
                        const nKey = `${n.row},${n.col}`
                        // 跳过公气点
                        if (!publicQiSet.has(nKey) && !visited.has(nKey)) {
                            stack.push(n)
                        }
                    } else {
                        borders.add(s)
                    }
                }
            }

            // 判断这片空地归属（严格判定：只有完全被一方包围的才算）
            let owner: "black" | "white" | "neutral" = "neutral"
            if (borders.size === 1) {
                const only = Array.from(borders)[0]
                if (only === "black" || only === "white") {
                    owner = only
                }
            }

            if (owner === "black") {
                blackArea += territory.size
            } else if (owner === "white") {
                whiteArea += territory.size
            } else {
                hasNeutralTerritory = true
            }
        }
    }

    // 平分双活公气点
    const publicQiCount = publicQiPoints.length
    if (publicQiCount > 0) {
        // 如果公气数量是奇数，平分后会出现0.5
        const halfPublicQi = publicQiCount / 2
        blackArea += halfPublicQi
        whiteArea += halfPublicQi
    }

    // 只计算围住的空地数，不计算棋子数
    const blackScore = blackArea
    const whiteScore = whiteArea
    const diff = blackScore - whiteScore // 可能是小数（如果有奇数个公气点）

    let winner: "black" | "white" | null = null
    if (diff > 0) {
        winner = "black"
    } else if (diff < 0) {
        winner = "white"
    }

    return {
        winner,
        diff,
    }
}

// 从历史落子记录重新推演整盘棋
// 用于悔棋后重建棋盘和提子数，保证结果与正常下棋逻辑一致
const replayFromHistory = (
    moves: MoveRecord[]
): {
    board: ChessType[][]
    currentPlayer: boolean
    blackCaptures: number
    whiteCaptures: number
    koPosition: ChessPosition | null
} => {
    // 初始状态：空棋盘，黑棋先手，提子数为 0
    let board: ChessType[][] = createEmptyBoard(BOARD_SIZE)
    let currentPlayer = true // true = 黑棋，false = 白棋
    let blackCaptures = 0
    let whiteCaptures = 0
    let koPosition: ChessPosition | null = null

    for (const move of moves) {
        const { row, col, skill } = move

        // 位置已被占用（理论上不应该发生），直接跳过该步
        if (board[row][col]) continue

        const newBoard = board.map((r) => [...r])
        const playerColor: ChessType = currentPlayer ? "black" : "white"
        const opponentColor: ChessType = currentPlayer ? "white" : "black"
        newBoard[row][col] = playerColor

        // 下子之前先检查打劫禁入点是否合理（理论上重放出来的历史不会违反）
        // 这里不做额外校验，只依据规则重建新的 koPosition

        // 检查并移除没有气的对方棋子
        const captureResult = captureOpponentPieces(
            newBoard,
            row,
            col,
            currentPlayer
        )
        let captured = captureResult.count
        const capturedPositions = captureResult.capturedPositions

        // 应用技能效果（如果有）
        if (skill && skill !== Skill.EMPTY) {
            const skillResult = applySkillEffect(
                newBoard,
                row,
                col,
                skill,
                playerColor,
                opponentColor,
                currentPlayer,
                move.skillPositions // 使用记录的位置（重放时）
            )
            captured += skillResult.captured
        }

        // 更新提子数
        if (currentPlayer) {
            blackCaptures += captured
        } else {
            whiteCaptures += captured
        }

        // 计算新的打劫禁入点（与 placeChess 保持一致）
        // 条件：本步只吃了一个子，且只吃了一个位置，且没有使用技能
        if (
            captured === 1 &&
            capturedPositions.length === 1 &&
            (!skill || skill === Skill.EMPTY)
        ) {
            koPosition = capturedPositions[0]
        } else {
            koPosition = null
        }

        // 确认落子并切换当前玩家
        board = newBoard
        currentPlayer = !currentPlayer
    }

    return {
        board,
        currentPlayer,
        blackCaptures,
        whiteCaptures,
        koPosition,
    }
}

// 获取所有可以落子的空位置
const getEmptyPositions = (board: ChessType[][]): ChessPosition[] => {
    const positions: ChessPosition[] = []
    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            if (board[row][col] === null) {
                positions.push({ row, col })
            }
        }
    }
    return positions
}

// 获取指定颜色的所有棋子位置
const getStonePositions = (
    board: ChessType[][],
    color: ChessType
): ChessPosition[] => {
    const positions: ChessPosition[] = []
    for (let row = 0; row < BOARD_SIZE; row++) {
        for (let col = 0; col < BOARD_SIZE; col++) {
            if (board[row][col] === color) {
                positions.push({ row, col })
            }
        }
    }
    return positions
}

// 随机选择N个元素
const randomSelect = <T>(arr: T[], count: number): T[] => {
    const shuffled = [...arr].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, Math.min(count, arr.length))
}

// 应用技能效果到棋盘，返回额外吃掉的棋子数和受影响的位置
const applySkillEffect = (
    board: ChessType[][],
    row: number,
    col: number,
    skill: Skill,
    playerColor: ChessType,
    opponentColor: ChessType,
    currentPlayer: boolean,
    predefinedPositions?: ChessPosition[] // 用于重放时使用预定义的位置
): { captured: number; positions: ChessPosition[] } => {
    let extraCaptured = 0
    const affectedPositions: ChessPosition[] = []

    switch (skill) {
        case Skill.SKILL1: {
            // 该棋子上下左右四个方向的棋子全部被击碎
            const directions = [
                [-1, 0], // 上
                [1, 0], // 下
                [0, -1], // 左
                [0, 1], // 右
            ]
            const { playBreakAnimation } = useAnimationStore.getState()

            for (const [dr, dc] of directions) {
                const newRow = row + dr
                const newCol = col + dc
                if (
                    newRow >= 0 &&
                    newRow < BOARD_SIZE &&
                    newCol >= 0 &&
                    newCol < BOARD_SIZE &&
                    board[newRow][newCol] !== null
                ) {
                    const chessColor = board[newRow][newCol]
                    // 播放破碎动画（击碎效果）
                    playBreakAnimation(
                        newRow,
                        newCol,
                        chessColor as "black" | "white"
                    )
                    board[newRow][newCol] = null
                    affectedPositions.push({ row: newRow, col: newCol })
                }
            }
            break
        }
        case Skill.SKILL2: {
            // 棋盘上随机5个可以落子的位置会额外出现己方棋子
            let selectedPositions: ChessPosition[]

            if (predefinedPositions && predefinedPositions.length > 0) {
                // 重放模式：使用预定义的位置
                selectedPositions = predefinedPositions
            } else {
                // 正常模式：随机选择
                const emptyPositions = getEmptyPositions(board)
                selectedPositions = randomSelect(emptyPositions, 5)
            }

            for (const pos of selectedPositions) {
                board[pos.row][pos.col] = playerColor
                affectedPositions.push(pos)
                const captureResult = captureOpponentPieces(
                    board,
                    pos.row,
                    pos.col,
                    currentPlayer
                )
                extraCaptured += captureResult.count
            }
            break
        }
        case Skill.SKILL3: {
            // 将对手棋盘上的5枚棋子随机传送到棋盘的其他位置
            // affectedPositions 格式：[from1, to1, from2, to2, ...]（偶数长度）
            if (predefinedPositions && predefinedPositions.length > 0) {
                // 重放模式：使用预定义的位置对
                for (let i = 0; i < predefinedPositions.length; i += 2) {
                    const from = predefinedPositions[i]
                    const to = predefinedPositions[i + 1]
                    if (to) {
                        board[from.row][from.col] = null
                        board[to.row][to.col] = opponentColor
                    }
                }
                affectedPositions.push(...predefinedPositions)
            } else {
                // 正常模式：随机选择
                const opponentPositions = getStonePositions(
                    board,
                    opponentColor
                )
                const selectedStones = randomSelect(opponentPositions, 5)
                const emptyPositions = getEmptyPositions(board)

                for (const stone of selectedStones) {
                    if (emptyPositions.length === 0) break
                    board[stone.row][stone.col] = null
                    const targetIndex = Math.floor(
                        Math.random() * emptyPositions.length
                    )
                    const target = emptyPositions[targetIndex]
                    board[target.row][target.col] = opponentColor
                    emptyPositions.splice(targetIndex, 1)
                    // 记录：原位置和目标位置成对
                    affectedPositions.push(stone, target)
                }
            }
            break
        }
        case Skill.SKILL4: {
            // 随机将对方棋盘上的5枚棋子变为己方棋子
            let selectedStones: ChessPosition[]

            if (predefinedPositions && predefinedPositions.length > 0) {
                // 重放模式：使用预定义的位置
                selectedStones = predefinedPositions
            } else {
                // 正常模式：随机选择
                const opponentPositions = getStonePositions(
                    board,
                    opponentColor
                )
                selectedStones = randomSelect(opponentPositions, 5)
            }

            for (const pos of selectedStones) {
                board[pos.row][pos.col] = playerColor
                affectedPositions.push(pos)
                const captureResult = captureOpponentPieces(
                    board,
                    pos.row,
                    pos.col,
                    currentPlayer
                )
                extraCaptured += captureResult.count
            }
            break
        }
        case Skill.SKILL5: {
            // 与该棋子相邻的所有棋子全部消失
            const neighbors = getNeighborsAsPositions(row, col)
            const { playBurnAnimation } = useAnimationStore.getState()

            for (const neighbor of neighbors) {
                const chessColor = board[neighbor.row][neighbor.col]
                if (chessColor !== null) {
                    // 播放燃烧动画（清除效果）
                    playBurnAnimation(
                        neighbor.row,
                        neighbor.col,
                        chessColor as "black" | "white"
                    )
                    board[neighbor.row][neighbor.col] = null
                    affectedPositions.push(neighbor)
                }
            }
            break
        }
    }

    return { captured: extraCaptured, positions: affectedPositions }
}

/**
 * 检查游戏是否应该结束，并计算得分和胜负状态
 *
 * 注意：此函数在每一步下完后被调用
 * 每一手为一次，计算当前步的分差，判断是否达到阈值，并更新历史记录
 *
 * @param board 棋盘状态
 * @param currentPlayer 当前玩家
 * @param currentStep 当前步数
 * @param blackCaptures 黑棋提子数
 * @param whiteCaptures 白棋提子数
 * @param consecutiveLargeDiffHistory 连续大分差历史记录（每个元素代表一步是否达到分差要求）
 * @param skipScoreRules 为 true 时跳过基于得分的结束判断（黑棋刚下完时使用，避免少白棋一手）
 * @returns { winner: "black" | "white" | null; scoreDiff: number; updatedHistory: boolean[] } 返回胜负状态、得分差和更新后的历史记录
 */
function checkGameEndAndCalculateScore(
    board: ChessType[][],
    currentPlayer: boolean,
    currentStep: number,
    blackCaptures: number,
    whiteCaptures: number,
    consecutiveLargeDiffHistory: boolean[],
    skipScoreRules?: boolean
): {
    winner: "black" | "white" | null
    scoreDiff: number
    updatedHistory: boolean[]
} {
    const endResult = shouldGameEnd(
        board,
        currentPlayer,
        currentStep,
        blackCaptures,
        whiteCaptures,
        consecutiveLargeDiffHistory,
        skipScoreRules
    )

    // 计算当前得分
    const scoreResult = calculateScore(board, blackCaptures, whiteCaptures)

    if (endResult.shouldEnd) {
        // 游戏应该结束，计算最终得分并设置胜负
        return {
            winner: scoreResult.winner,
            scoreDiff: scoreResult.diff,
            updatedHistory: endResult.updatedHistory,
        }
    } else {
        // 游戏未结束，清除胜负状态，但保留更新后的历史记录
        return {
            winner: null,
            scoreDiff: 0,
            updatedHistory: endResult.updatedHistory,
        }
    }
}

export const useGameStore = create<GameState & GameActions>((set, get) => ({
    chooseSkills: new Array(3).fill(Skill.EMPTY),
    activeSkillIndex: null,
    usedSkillIndices: [],
    currentStep: 0,
    currentPlayer: true, // 黑棋先手
    board: createEmptyBoard(BOARD_SIZE),
    history: [],
    blackCaptures: 0,
    whiteCaptures: 0,
    resetGame: false,
    winner: null, // 初始未分出胜负
    scoreDiff: 0, // 初始得分差为 0
    gameStartTime: 0,
    koPosition: null, // 初始没有打劫禁入点
    consecutiveLargeDiffHistory: [], // 连续大分差历史记录（每个元素代表一个回合是否达到分差要求）
    validateMove: (row: number, col: number) => {
        const {
            board,
            currentPlayer,
            activeSkillIndex,
            chooseSkills,
            koPosition,
        } = get()

        const activeSkill =
            activeSkillIndex !== null ? chooseSkills[activeSkillIndex] : null
        const isSuicideImmuneSkill =
            activeSkill === Skill.SKILL1 || activeSkill === Skill.SKILL5

        // 边界检查
        if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
            return { success: false, reason: "outOfBounds" }
        }

        // 已有棋子检查
        if (board[row][col]) {
            return { success: false, reason: "occupied" }
        }

        // 普通落子需要遵守打劫和自杀规则；
        // 技能1 / 技能5 会清除相邻棋子，允许下在原本属于自杀或打劫的位置。
        if (!isSuicideImmuneSkill) {
            // 打劫检查
            if (
                koPosition &&
                koPosition.row === row &&
                koPosition.col === col
            ) {
                return { success: false, reason: "ko" }
            }

            // 自杀检查
            if (
                !isValidMoveForStore(board, row, col, currentPlayer, koPosition)
            ) {
                return { success: false, reason: "suicide" }
            }
        }

        return { success: true }
    },
    placeChess: (row: number, col: number) => {
        const {
            board,
            currentPlayer,
            history,
            blackCaptures,
            whiteCaptures,
            currentStep,
            activeSkillIndex,
            chooseSkills,
            koPosition,
            consecutiveLargeDiffHistory,
        } = get()

        // 获取当前激活的技能
        const activeSkill =
            activeSkillIndex !== null ? chooseSkills[activeSkillIndex] : null
        const isSuicideImmuneSkill =
            activeSkill === Skill.SKILL1 || activeSkill === Skill.SKILL5

        if (!activeSkill) {
            sfxPlay("CHESS")
        }
        // 检查位置是否有效
        if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
            return { success: false, reason: "outOfBounds" }
        }

        // 检查位置是否已有棋子
        if (board[row][col]) {
            return { success: false, reason: "occupied" }
        }

        // 普通落子需要遵守打劫和自杀规则；
        // 技能1 / 技能5 会清除相邻棋子，允许下在原本属于自杀或打劫的位置。
        if (!isSuicideImmuneSkill) {
            // 检查是否是打劫禁入点
            if (
                koPosition &&
                koPosition.row === row &&
                koPosition.col === col
            ) {
                return { success: false, reason: "ko" }
            }

            // 检查自杀规则：使用统一的合法性检查函数
            if (
                !isValidMoveForStore(board, row, col, currentPlayer, koPosition)
            ) {
                return { success: false, reason: "suicide" }
            }
        }

        // 创建新的棋盘副本
        const newBoard = board.map((r) => [...r])

        // 放置棋子
        const playerColor: ChessType = currentPlayer ? "black" : "white"
        const opponentColor: ChessType = currentPlayer ? "white" : "black"
        newBoard[row][col] = playerColor

        // 检查并移除没有气的对方棋子
        const captureResult = captureOpponentPieces(
            newBoard,
            row,
            col,
            currentPlayer
        )
        let captured = captureResult.count
        const capturedPositions = captureResult.capturedPositions

        // 应用技能效果
        let skillPositions: ChessPosition[] | undefined
        if (activeSkill && activeSkill !== Skill.EMPTY) {
            const skillResult = applySkillEffect(
                newBoard,
                row,
                col,
                activeSkill,
                playerColor,
                opponentColor,
                currentPlayer
            )
            captured += skillResult.captured
            skillPositions = skillResult.positions
        }

        // 更新提子数
        const newBlackCaptures = currentPlayer
            ? blackCaptures + captured // 黑棋回合，黑棋提子数增加
            : blackCaptures // 白棋回合，黑棋提子数不变

        const newWhiteCaptures = currentPlayer
            ? whiteCaptures // 黑棋回合，白棋提子数不变
            : whiteCaptures + captured

        const newUsedSkillIndices =
            activeSkillIndex !== null
                ? [...get().usedSkillIndices, activeSkillIndex]
                : get().usedSkillIndices

        // 计算新的打劫禁入点
        // 打劫规则：如果只吃了一个子（且只吃了一个位置），且没有使用技能，记录被吃的位置
        let newKoPosition: ChessPosition | null = null
        if (
            captured === 1 &&
            capturedPositions.length === 1 &&
            (!activeSkill || activeSkill === Skill.EMPTY)
        ) {
            // 记录被吃的位置作为打劫禁入点
            newKoPosition = capturedPositions[0]
        }

        // 切换玩家后检查游戏是否应该结束（异步）
        // 注意：每一步下完后都要检查游戏是否应该结束
        // 每一手为一次，计算当前步的分差，判断是否达到阈值，并更新历史记录
        const nextPlayer = !currentPlayer

        // 黑下完：只做规则 A（无子可下）+ 更新连续大分差历史，不跑规则 C（避免少白棋一手就判分）
        // 白下完：做完整结束判断（规则 A + 规则 C）。仍需统一走 checkGameEndAndCalculateScore 以复用规则 A 与 updatedHistory
        const skipScoreRules = currentPlayer === true // currentPlayer 为本手落子方，true=黑刚下完
        const endResult = checkGameEndAndCalculateScore(
            newBoard,
            nextPlayer,
            currentStep + 1,
            newBlackCaptures,
            newWhiteCaptures,
            consecutiveLargeDiffHistory,
            skipScoreRules
        )

        set({
            board: newBoard,
            currentPlayer: nextPlayer,
            history: [
                ...history,
                activeSkill && activeSkill !== Skill.EMPTY
                    ? {
                          row,
                          col,
                          skill: activeSkill,
                          skillPositions,
                          capturedCount: captured,
                      }
                    : {
                          row,
                          col,
                          capturedCount: captured,
                      },
            ],
            blackCaptures: newBlackCaptures,
            whiteCaptures: newWhiteCaptures,
            currentStep: currentStep + 1,
            activeSkillIndex: null, // 技能使用后清除
            usedSkillIndices: newUsedSkillIndices, // 更新已使用技能槽位索引列表
            koPosition: newKoPosition, // 更新打劫禁入点
            winner: endResult.winner,
            scoreDiff: endResult.scoreDiff,
            consecutiveLargeDiffHistory: endResult.updatedHistory,
        })

        return { success: true }
    },

    undo: () => {
        const { history, currentStep } = get()
        if (currentStep <= MIN_STEP) return

        // 由于现在是「玩家 + AI」轮流下棋，
        // 一次悔棋默认撤销最近的两步（如果不足两步，则尽可能多撤销）
        const stepsToUndo = Math.min(2, history.length)
        const newHistory = history.slice(0, history.length - stepsToUndo)

        // 根据剩余历史记录重放整盘棋，得到新的棋盘和提子数
        const replayResult = replayFromHistory(newHistory)

        // 重新计算已使用的技能槽位索引（从剩余历史中反推）
        const { chooseSkills } = get()
        const newUsedSkillIndices: number[] = []

        for (const move of newHistory) {
            if (move.skill && move.skill !== Skill.EMPTY) {
                // 找到该技能在 chooseSkills 中的索引（只查找未使用过的）
                for (let i = 0; i < chooseSkills.length; i++) {
                    if (
                        chooseSkills[i] === move.skill &&
                        !newUsedSkillIndices.includes(i)
                    ) {
                        newUsedSkillIndices.push(i)
                        break
                    }
                }
            }
        }

        // 悔棋时，从连续大分差历史记录中删除对应数量的元素
        // 注意：每一手为一次，所以每撤销1步，需要删除1个历史记录元素
        const { consecutiveLargeDiffHistory } = get()
        const updatedHistory = consecutiveLargeDiffHistory.slice(
            0,
            Math.max(0, consecutiveLargeDiffHistory.length - stepsToUndo)
        )

        // 能悔棋说明棋局未结束，无需再检查；直接恢复为进行中状态
        set({
            ...replayResult,
            history: newHistory,
            currentStep: currentStep - stepsToUndo,
            activeSkillIndex: null, // 悔棋时清除激活的技能
            usedSkillIndices: newUsedSkillIndices, // 恢复已使用技能槽位索引列表
            winner: null,
            scoreDiff: 0,
            consecutiveLargeDiffHistory: updatedHistory,
        })
    },

    reset: () => {
        set({
            board: createEmptyBoard(BOARD_SIZE),
            currentPlayer: true,
            history: [],
            blackCaptures: 0,
            whiteCaptures: 0,
            currentStep: 0,
            activeSkillIndex: null,
            chooseSkills: new Array(3).fill(Skill.EMPTY), // 重置已选技能
            usedSkillIndices: [], // 重置已使用技能槽位索引
            resetGame: !get().resetGame,
            winner: null, // 重置胜负状态
            scoreDiff: 0, // 重置得分差
            koPosition: null, // 重置打劫禁入点
            consecutiveLargeDiffHistory: [], // 重置连续大分差历史记录
        })
        // 重置时清空所有棋子动画，避免残留的技能动画继续播放
        useAnimationStore.getState().clearAll()
    },

    togglePlayer: () => {
        set((state) => ({
            currentPlayer: !state.currentPlayer,
        }))
    },

    setChooseSkills: (chooseSkills: Skill[]) => {
        set({
            chooseSkills: [
                ...chooseSkills.filter((skill) => skill !== Skill.EMPTY), // 非空技能在前
                ...chooseSkills.filter((skill) => skill === Skill.EMPTY), // 空技能在后
            ],
        })
    },

    useSkill: (skillIndex: number) => {
        const { chooseSkills, usedSkillIndices } = get()
        const skill = chooseSkills[skillIndex]
        if (
            skill === Skill.EMPTY ||
            skill === undefined ||
            usedSkillIndices.includes(skillIndex)
        ) {
            return // 空技能、无效索引或已使用过的技能槽位，不能使用
        }
        set({ activeSkillIndex: skillIndex })
    },
    clearActiveSkill: () => {
        set({ activeSkillIndex: null })
    },
    setBoard: (board: ChessType[][]) => {
        set({ board })
    },
    clearBoard: () => {
        set({ board: createEmptyBoard(BOARD_SIZE) })
    },
    setGameStartTime: (gameStartTime: number) => {
        set({ gameStartTime })
    },
}))
